import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ["remainingText"]

  static values = {
    target: String
  }

  initialize(){
    this.$textEntry = this.findTargetInput()

    this.minLength = this.$textEntry.minLength
    this.maxLength = this.$textEntry.maxLength

    if (this.maxLength === -1) {
      const errorMsg = `
        The input associated with the input text counter
        doesn't have a \`maxlength\` property defined, but it is required.
        If there is a maxlength defined in the server HTML, this
        initialization code may be running twice for the same element.
        Check if there are multiple elements with the selector ${this.targetValue}.
      `
      throw new Error(errorMsg)
    }

    this.setupInitialState()

    this.$textEntry.addEventListener("keyup", () => { this.updateCount() })
    this.updateCount()
  }

  findTargetInput(){
    const textEntrySelector = this.targetValue
    const input = document.querySelector(textEntrySelector)

    if(input === null) {
      let errorMsg = "The input text counter element must have a target selector representing an element on this page."
      if(textEntrySelector === undefined) {
        errorMsg += " There was no `target` defined."
      } else {
        errorMsg += ` There is no element with the selector '${textEntrySelector}'.`
      }
      throw new Error(errorMsg)
    }

    return input
  }

  setupInitialState(){
    // Adding 'pristine' is a way of preventing the CSS for 'invalid'
    // being applied to the element until we remove the class below.
    this.remainingTextTarget.classList.add("pristine")

    // We don't want 'extra' text entry to be prevented by the input, so we remove these
    // attributes. Instead we'll prevent submissions using JS, along with
    // providing visual cues.
    if(this.$textEntry.hasAttribute(this.minLength)) this.$textEntry.attributes.removeNamedItem("minlength")
    this.$textEntry.attributes.removeNamedItem("maxlength")
  }

  updateCount(){
    const text = this.$textEntry.value
    const currentLength = text.trim().length
    const remainingAllowedCharacters = this.maxLength - currentLength
    this.remainingTextTarget.innerHTML = remainingAllowedCharacters.toLocaleString() + " characters remaining"

    this.$textEntry.setCustomValidity("")
    this.remainingTextTarget.classList.remove("invalid")

    if(currentLength === 0 && !this.$textEntry.required){
      // do nothing
    } else if(this.minLength !== undefined && currentLength < this.minLength) {
      this.$textEntry.setCustomValidity(`Must be at least ${this.minLength} characters.`)
      this.remainingTextTarget.classList.add("invalid")
    } else if (this.maxLength !== undefined && currentLength > this.maxLength) {
      this.$textEntry.setCustomValidity(`Must be at maximum ${this.maxLength} characters.`)
      this.remainingTextTarget.classList.add("invalid")
    }

    if(this.minLength !== undefined && currentLength >= this.minLength) {
      // once the text has reached the minimum length, we remove the
      // 'pristine' class so that the 'invalid' CSS can be applied if the
      // length goes below the minLength
      this.remainingTextTarget.classList.remove("pristine")
    }
  }
}
